import { useState, type FormEvent } from "react";
import { useAuth } from "../auth";
import { auth } from "../firebase";
import { consoleConfig, signInWithConsole, SSO_ERROR_LABEL, SsoError } from "../lib/console-sso";
import { LangSwitch, useI18n } from "../lib/i18n";

// AI店長コンソールのログインで入るボタン(#357)。設定(web/.env の VITE_AI_TENCHO_*)が無ければ出さない
const CONSOLE = consoleConfig();

export function Login() {
  const { signInWithGoogle, sendLink } = useAuth();
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onGoogle() {
    setError(null);
    setBusy(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // プロバイダ未有効の場合は原因が分かる文言にする
      setError(
        msg.includes("operation-not-allowed")
          ? t("Google サインインがまだ有効化されていません。Firebase コンソールで有効にしてください。")
          : msg,
      );
    } finally {
      setBusy(false);
    }
  }

  async function onConsole() {
    if (!CONSOLE) return;
    setError(null);
    setBusy(true);
    try {
      // ポップアップは押した処理の中で開く(signInWithConsole は await より先に開く)
      await signInWithConsole(auth, CONSOLE, import.meta.env.VITE_FIREBASE_PROJECT_ID);
    } catch (err) {
      setError(err instanceof SsoError ? t(SSO_ERROR_LABEL[err.code]) : err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await sendLink(email.trim());
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="center">
      <div className="card narrow">
        <div className="login-lang"><LangSwitch /></div>
        <h1>{t("イチニンマエ")}</h1>

        {sent ? (
          <>
            <p className="ok">
              {t("{email} にログイン用のリンクを送りました。", { email })}
            </p>
            <p className="muted small">
              {t("数分待っても届かない場合は迷惑メールをご確認ください。それでも届かないときは Google でログインしてください。")}
            </p>
            <button className="link" onClick={() => { setSent(false); setShowEmail(false); }}>
              {t("戻る")}
            </button>
          </>
        ) : (
          <>
            <p className="muted">{t("社内アカウントでログインしてください。")}</p>
            <button onClick={onGoogle} disabled={busy}>
              {busy ? t("処理中…") : t("Google でログイン")}
            </button>
            {CONSOLE && (
              <button onClick={onConsole} disabled={busy}>
                {t("AI店長でログイン")}
              </button>
            )}

            {!showEmail ? (
              <button className="link" onClick={() => setShowEmail(true)}>
                {t("Google アカウントが無い方はこちら")}
              </button>
            ) : (
              <form onSubmit={onSubmit}>
                <label htmlFor="email">{t("メールアドレス")}</label>
                <input
                  id="email"
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
                <button type="submit" disabled={busy || !email.trim()}>
                  {busy ? t("送信中…") : t("ログインリンクを送る")}
                </button>
                <p className="muted small">
                  {t("※ 現在この方式はメールが届かない不具合が確認されています。可能な限り Google でのログインをお使いください。")}
                </p>
              </form>
            )}

            {error && <p className="error">{error}</p>}
          </>
        )}
      </div>
    </div>
  );
}
